"use client";

import type { ReactNode } from "react";
import { useEffect } from "react";

declare global {
	interface Window {
		__odinSWState?: {
			status: "idle" | "registering" | "registered" | "ready" | "error";
			scope: string | null;
			activeScriptUrl: string | null;
			error: string | null;
		};
	}
}

interface SerwistProviderProps {
	swUrl: string;
	options?: RegistrationOptions;
	children?: ReactNode;
}

const emitSWState = () => {
	window.dispatchEvent(new CustomEvent("odin:sw-state"));
};

const updateSWState = (
	state: Partial<NonNullable<Window["__odinSWState"]>>,
) => {
	window.__odinSWState = {
		status: "idle",
		scope: null,
		activeScriptUrl: null,
		error: null,
		...window.__odinSWState,
		...state,
	};
	emitSWState();
};

export function SerwistProvider({
	swUrl,
	options,
	children,
}: SerwistProviderProps) {
	useEffect(() => {
		if (!("serviceWorker" in navigator)) {
			updateSWState({
				status: "error",
				error: "serviceWorker unsupported",
			});
			return;
		}

		let cancelled = false;

		const syncRegistrationState = async () => {
			try {
				const registration = await navigator.serviceWorker.getRegistration(
					options?.scope,
				);
				if (!registration || cancelled) return;

				updateSWState({
					status: "registered",
					scope: registration.scope ?? null,
					activeScriptUrl:
						registration.active?.scriptURL ??
						registration.waiting?.scriptURL ??
						registration.installing?.scriptURL ??
						null,
					error: null,
				});

				const readyRegistration = await navigator.serviceWorker.ready;
				if (cancelled) return;

				updateSWState({
					status: "ready",
					scope: readyRegistration.scope ?? registration.scope ?? null,
					activeScriptUrl:
						readyRegistration.active?.scriptURL ??
						registration.active?.scriptURL ??
						registration.waiting?.scriptURL ??
						registration.installing?.scriptURL ??
						null,
					error: null,
				});
			} catch (error) {
				if (cancelled) return;
				updateSWState({
					status: "error",
					error: error instanceof Error ? error.message : String(error),
				});
			}
		};

		const register = async () => {
			try {
				updateSWState({ status: "registering", error: null });
				const registration = await navigator.serviceWorker.register(swUrl, {
					scope: options?.scope ?? "/",
					updateViaCache: options?.updateViaCache,
				});

				if (cancelled) return;

				updateSWState({
					status: "registered",
					scope: registration.scope ?? null,
					activeScriptUrl:
						registration.active?.scriptURL ??
						registration.waiting?.scriptURL ??
						registration.installing?.scriptURL ??
						null,
					error: null,
				});

				void syncRegistrationState();
			} catch (error) {
				if (cancelled) return;
				updateSWState({
					status: "error",
					error: error instanceof Error ? error.message : String(error),
				});
			}
		};

		void register();

		const handleControllerChange = () => {
			void syncRegistrationState();
		};
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				void syncRegistrationState();
			}
		};

		navigator.serviceWorker.addEventListener(
			"controllerchange",
			handleControllerChange,
		);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			cancelled = true;
			navigator.serviceWorker.removeEventListener(
				"controllerchange",
				handleControllerChange,
			);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [options?.scope, options?.updateViaCache, swUrl]);

	return children ?? null;
}
